import { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Chip, CircularProgress, Alert, Button, Table,
  TableBody, TableCell, TableHead, TableRow, Tooltip, Snackbar, Divider, TextField,
  Accordion, AccordionSummary, AccordionDetails, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, Link as MuiLink,
} from '@mui/material';
import {
  Insights, Refresh, Send, Telegram, TrendingDown, CheckCircle, InfoOutlined, RocketLaunch,
  ExpandMore, HelpOutline, Business, OpenInNew, Article,
} from '@mui/icons-material';
import apiService from '../services/api';

const ACCENT = '#8B5CF6';

const SRC_COLOR: Record<string, string> = {
  'Reco engine': '#10B981', 'Volatility BUY': '#3B82F6', 'Oversold dip': '#F59E0B',
  'Positive news': '#A78BFA', 'Media pick': '#EC4899',
};

function fmtPrice(v: number | null): string { return v != null ? `$${v.toFixed(2)}` : '—'; }

function fmtAge(isoUtc?: string): string {
  if (!isoUtc) return '';
  const d = new Date(isoUtc.endsWith('Z') ? isoUtc : isoUtc + 'Z');
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 2) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (mins < 1440) return `${Math.round(mins / 60)}h ago`;
  return `${Math.round(mins / 1440)}d ago`;
}

function SectionMeta({ genAt }: { genAt?: string }) {
  if (!genAt) return null;
  const d = new Date(genAt.endsWith('Z') ? genAt : genAt + 'Z');
  const localStr = d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  return (
    <Tooltip title={`Calculated at ${d.toLocaleString()}`} placement="right">
      <Typography variant="caption" color="text.disabled"
        sx={{ display: 'inline-block', mb: 1, ml: 0.5, cursor: 'default', fontSize: '0.68rem' }}>
        ⏱ {localStr} ({fmtAge(genAt)})
      </Typography>
    </Tooltip>
  );
}

function fmtMarketCap(v?: number | null): string {
  if (v == null) return '—';
  if (v >= 1e12) return `$${(v / 1e12).toFixed(2)}T`;
  if (v >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
  return `$${v.toLocaleString()}`;
}

/**
 * Info icon → what the company actually does.
 *
 * A ticker and a score say nothing about the business, which matters most for exactly
 * these emerging names where the symbol is unfamiliar. Fetched on open rather than with
 * the table so a 60-row list doesn't trigger 60 profile lookups nobody asked for.
 */
function CompanyInfoButton({ symbol, color }: { symbol: string; color: string }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      setData(await apiService.getCompanyProfile(symbol));
    } catch (e: any) {
      setErr(e?.message || 'Could not load company information.');
    } finally {
      setLoading(false);
    }
  }, [symbol]);

  const handleOpen = () => { setOpen(true); if (!data) load(); };

  return (
    <>
      <Tooltip title={`What does ${symbol} do?`}>
        <IconButton size="small" onClick={handleOpen}
          sx={{ p: 0.25, color: color, '&:hover': { bgcolor: color + '22' } }}>
          <InfoOutlined sx={{ fontSize: 17 }} />
        </IconButton>
      </Tooltip>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Business sx={{ color }} />
            <Box>
              <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                {data?.name || symbol}
              </Typography>
              <Typography variant="caption" color="text.secondary">{symbol}</Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          )}
          {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}

          {!loading && data && (
            <>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
                {data.sector && <Chip size="small" label={data.sector}
                  sx={{ bgcolor: color + '22', color, fontWeight: 600 }} />}
                {data.industry && <Chip size="small" label={data.industry} variant="outlined" />}
                {data.country && <Chip size="small" label={data.country} variant="outlined" />}
              </Box>

              <Box sx={{ display: 'flex', gap: 3, mb: 2, flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="caption" color="text.disabled" display="block">Market cap</Typography>
                  <Typography variant="body2" fontWeight={600}>{fmtMarketCap(data.market_cap)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.disabled" display="block">Employees</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {data.employees != null ? data.employees.toLocaleString() : '—'}
                  </Typography>
                </Box>
              </Box>

              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                What the company does
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
                {data.business_summary || 'No business description is available for this symbol.'}
              </Typography>

              {data.website && (
                <MuiLink href={data.website} target="_blank" rel="noopener noreferrer"
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mt: 2, fontSize: '0.85rem' }}>
                  {data.website.replace(/^https?:\/\//, '')} <OpenInNew sx={{ fontSize: 14 }} />
                </MuiLink>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button size="small" onClick={load} disabled={loading}>Refresh</Button>
          <Button size="small" onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

/**
 * The articles that put this stock on the list, as links.
 *
 * Future Technology picks originate in "top stocks to buy" pieces from Motley Fool, Yahoo
 * Finance and Seeking Alpha; the pipeline knew which article surfaced each name but only
 * kept its title, so the claim could never be checked against the source. Multiple
 * publications naming the same stock is itself the stronger signal, so all of them show.
 */
function SourceArticles({ articles, provenance }: { articles: any[]; provenance?: string }) {
  if (!articles || articles.length === 0) {
    // No citation means the stock reached the list by keyword-matching its business
    // summary, with no article and no LLM check. Labelled rather than left blank so a
    // thematic guess is never read as a sourced recommendation.
    const kw = provenance !== 'article';
    return (
      <Tooltip title={kw
        ? 'Matched on its business description — no article recommended this stock, and no LLM confirmed the thesis. Weaker evidence than a cited pick.'
        : 'Confirmed by the classifier, but the citing article predates link capture.'}>
        <Chip size="small" label={kw ? 'keyword match' : 'no link stored'} variant="outlined"
          sx={{ height: 17, fontSize: '0.58rem', color: 'text.disabled', borderColor: '#ffffff22' }} />
      </Tooltip>
    );
  }
  const label = (a: any) => (a.source || 'source')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c: string) => c.toUpperCase());

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
      {articles.slice(0, 3).map((a: any, i: number) => (
        <Tooltip key={i} title={a.title || ''} placement="left">
          <span>
            {a.url ? (
              <MuiLink href={a.url} target="_blank" rel="noopener noreferrer"
                sx={{
                  display: 'inline-flex', alignItems: 'center', gap: 0.3,
                  fontSize: '0.65rem', color: '#A855F7', textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}>
                <Article sx={{ fontSize: 12 }} />
                {label(a)}
                <OpenInNew sx={{ fontSize: 10 }} />
              </MuiLink>
            ) : (
              <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
                {label(a)}
              </Typography>
            )}
          </span>
        </Tooltip>
      ))}
    </Box>
  );
}

function PriceCell({ price, asOf }: { price: number | null; asOf?: string }) {
  const priceStr = fmtPrice(price);
  if (!asOf) return <>{priceStr}</>;
  const d = new Date(asOf);
  const dateStr = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  return (
    <Tooltip title={`Close price as of ${asOf}`} placement="top">
      <Box component="span" sx={{ cursor: 'default' }}>
        {priceStr}
        <Typography component="span" variant="caption" color="text.disabled" sx={{ ml: 0.4, fontSize: '0.62rem' }}>
          {dateStr}
        </Typography>
      </Box>
    </Tooltip>
  );
}

// Symbol with a company · sector hover tooltip (matches the rest of the app).
function Ticker({ symbol, meta, color }: { symbol: string; meta: Record<string, any>; color?: string }) {
  const m = meta[symbol];
  const title = m?.name ? `${m.name}${m.sector ? ` · ${m.sector}` : ''}` : symbol;
  return (
    <Tooltip title={title} placement="right" arrow>
      <Box component="span" sx={{ fontWeight: 700, cursor: 'default', color }}>{symbol}</Box>
    </Tooltip>
  );
}

export default function TopPicks() {
  const [picks, setPicks] = useState<any[]>([]);
  const [actionBuys, setActionBuys] = useState<any[]>([]);
  const [actionExits, setActionExits] = useState<any[]>([]);
  const [rotation, setRotation] = useState<any[]>([]);
  const [momentum, setMomentum] = useState<any[]>([]);
  const [emerging, setEmerging] = useState<any[]>([]);
  const [exits, setExits] = useState<any[]>([]);
  const [exitInput, setExitInput] = useState('');
  const [tg, setTg] = useState<any>(null);
  const [meta, setMeta] = useState<Record<string, { name: string; sector: string }>>({});
  const [sectGenAt, setSectGenAt] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sectionErrors, setSectionErrors] = useState<Record<string, string>>({});
  const [toast, setToast] = useState<string | null>(null);

  const load = useCallback(async (forceRefresh = false) => {
    if (forceRefresh) setRefreshing(true);
    setError(null);
    setSectionErrors({});

    // Load all sections in parallel; auto-retry 502s (tunnel/cold-cache); track per-section errors.
    const settle = async <T,>(key: string, fn: () => Promise<T>, fallback: T): Promise<T> => {
      const MAX_RETRIES = 2;
      for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        try { return await fn(); }
        catch (e: any) {
          const status = e?.response?.status ?? 0;
          if (status === 502 && attempt < MAX_RETRIES) {
            await new Promise(r => setTimeout(r, 4000 * (attempt + 1))); // 4s, 8s
            continue;
          }
          const msg = e?.response?.data?.detail || e?.message || 'Load failed';
          setSectionErrors(prev => ({ ...prev, [key]: msg }));
          return fallback;
        }
      }
      return fallback;
    };

    const [tp, rr, mo, em, ex, ab, st] = await Promise.all([
      settle('picks',    () => apiService.getTopPicks(20, forceRefresh),                      { picks: [] }),
      settle('rotation', () => apiService.getRotationRadar(20),                               { candidates: [] }),
      settle('momentum', () => apiService.getMomentumLeaders(20, forceRefresh),               { leaders: [] }),
      settle('emerging', () => apiService.getEmergingBreakouts(60, false, forceRefresh), { breakouts: [] }),
      settle('exits',    () => apiService.getExitSignals(),                                   { signals: [] }),
      settle('board',    () => apiService.getActionBoard(),                                   { buys: [], exits: [] }),
      settle('tg',       () => apiService.getTelegramStatus(),                                null),
    ]);

    setPicks((tp as any).picks ?? []);
    setRotation((rr as any).candidates ?? []);
    setMomentum((mo as any).leaders ?? []);
    setEmerging((em as any).breakouts ?? []);
    setExits((ex as any).signals ?? []);
    setActionBuys((ab as any).buys ?? []);
    setActionExits((ab as any).exits ?? []);
    setTg(st);
    setSectGenAt({
      picks:    (tp as any).generated_at ?? '',
      rotation: (rr as any).generated_at ?? '',
      momentum: (mo as any).generated_at ?? '',
      emerging: (em as any).generated_at ?? '',
      exits:    (ex as any).generated_at ?? '',
      board:    (ab as any).generated_at ?? '',
    });
    setLoading(false);
    setRefreshing(false);

    // Fetch company/sector for tooltips (chunks of 50, best-effort).
    const symbols = Array.from(new Set([
      ...((tp as any).picks ?? []).map((p: any) => p.symbol),
      ...((rr as any).candidates ?? []).map((c: any) => c.symbol),
      ...((mo as any).leaders ?? []).map((l: any) => l.symbol),
      ...((em as any).breakouts ?? []).map((b: any) => b.symbol),
    ]));
    for (let i = 0; i < symbols.length; i += 50) {
      apiService.getTickerInfo(symbols.slice(i, i + 50))
        .then(info => setMeta(prev => ({ ...prev, ...info })))
        .catch(() => {});
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const checkExits = async () => {
    try {
      const ex = await apiService.getExitSignals(exitInput.trim() || undefined);
      setExits(ex.signals ?? []);
    } catch (e: any) { setError(e.response?.data?.detail || 'Exit check failed'); }
  };

  const send = async () => {
    setSending(true);
    try {
      const r = await apiService.sendTopPicksTelegram(10);
      setToast(`Sent to Telegram (message #${r.message_id})`);
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Telegram send failed');
    } finally { setSending(false); }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Insights sx={{ color: ACCENT, fontSize: 30 }} />
          <Box>
            <Typography variant="h5" fontWeight="bold">Top Picks</Typography>
            <Typography variant="caption" color="text.secondary">
              The strongest candidates across every feature, ranked by how many independent signals agree.
            </Typography>
          </Box>
        </Box>
        <Button variant="outlined" startIcon={refreshing ? <CircularProgress size={14} color="inherit" /> : <Refresh />}
          onClick={() => load(true)} disabled={refreshing} sx={{ color: ACCENT, borderColor: `${ACCENT}55` }}>
          Refresh
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {Object.keys(sectionErrors).length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setSectionErrors({})}>
          Some sections failed to load — data may be stale or the server is busy (a background scan may be running).
          {Object.entries(sectionErrors).map(([k, v]) => (
            <Box key={k} sx={{ fontSize: '0.75rem', mt: 0.3 }}><strong>{k}</strong>: {v}</Box>
          ))}
          <Box sx={{ mt: 0.5 }}>Try the <strong>Refresh</strong> button to retry.</Box>
        </Alert>
      )}

      {/* How to read this page */}
      <Accordion sx={{ mb: 3, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider',
        borderRadius: '8px !important', '&:before': { display: 'none' } }}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HelpOutline sx={{ color: ACCENT }} /> How to read this page (the trade lifecycle)
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.7 }}>
            The five tables map a stock's whole journey — <strong>find it early → confirm it → buy quality dips →
            manage the winner → exit at the top</strong>. The power is in how they overlap.
          </Typography>
          {[
            ['🟣 Emerging Breakouts — "before it flies"', '#A855F7',
              'Earliest stage. Future-tech names breaking out of a long base with only a modest run and volume just waking up. Use as a watchlist for fresh entries. Higher reward, higher uncertainty — a base breakout can still fail.'],
            ['🟢 Momentum / Emerging-Leader', '#22C55E',
              'Confirmed leaders already near 52-week highs with strong 3-/6-month trends. The trend is proven, but you\'re buying higher. Best treated as "in motion" — enter on pullbacks, not by chasing a vertical move.'],
            ['🔵 Consensus Top Picks', ACCENT,
              'Ranked by how many INDEPENDENT features agree (2× < 3× < 4×). Your highest-conviction research shortlist. Read the Detail column: "▲X% target" = reco-engine upside, "% of range" = where it sits (low = oversold, high = extended). Tends toward bigger, well-covered names.'],
            ['🟢 Momentum × Consensus (top banner)', '#22C55E',
              'The sweet spot — a momentum leader that another independent feature also flags. Durable trend + confirmation = the strongest combination on the page.'],
            ['🟠 Rotation / Oversold-Reversion', '#F59E0B',
              'Contrarian. Quality names at the BOTTOM of their range with no bad news — possible flow-driven dips to buy. Verify it\'s a temporary dip, not a broken company.'],
            ['🔴 Exit / Sell Signals', '#EF4444',
              'When to get out. HOLD = trend intact, let it run (a peak can be months away). TRIM = overbought/extended, take some profit & tighten stop. SELL = trend broke (below 50-day MA) or −22% from its peak. Type your own tickers to check holdings.'],
          ].map(([t, c, d]) => (
            <Box key={t as string} sx={{ mb: 1.2, pl: 1.2, borderLeft: `3px solid ${c}` }}>
              <Typography variant="body2" fontWeight="bold" sx={{ color: c as string }}>{t}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5 }}>{d}</Typography>
            </Box>
          ))}
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>When a ticker shows up in two tables:</Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.5 } }}>
            <Typography component="li" variant="caption" color="text.secondary"><strong>Emerging → Momentum</strong>: graduated from early to confirmed leader — the ideal progression. Strongest if you caught it in Emerging first.</Typography>
            <Typography component="li" variant="caption" color="text.secondary"><strong>Momentum + Exit (TRIM)</strong> = e.g. <strong>ALOT</strong>: a real leader that's gone parabolic/overbought. Don't chase a new buy; if you hold it, take partial profit & tighten the stop. Wait for a pullback to re-enter.</Typography>
            <Typography component="li" variant="caption" color="text.secondary"><strong>Momentum/Consensus + Exit (SELL)</strong>: a former leader whose trend just broke — the exit overrides the buy signal. Reduce/close.</Typography>
            <Typography component="li" variant="caption" color="text.secondary"><strong>Consensus + Rotation (Oversold)</strong>: a multi-feature pick that's also dipped — a quality name on sale (buy-the-dip), the lower-risk entry type.</Typography>
          </Box>
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1.5 }}>
            These are mechanical signals to <em>research and act on with stops</em> — not predictions or advice.
          </Typography>
        </AccordionDetails>
      </Accordion>

      {/* Action Board — the synthesis */}
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Insights sx={{ color: ACCENT }} /> Action Board — what to do now
      </Typography>
      <SectionMeta genAt={sectGenAt.board} />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Every ticker's cross-table overlaps synthesized into one action + conviction (a signal-agreement score, <em>not</em> a win probability). Exit signals override buys.
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 4 }}>
        {[['🟢 Buy / Hold', actionBuys, '#22C55E'], ['🔻 Trim / Sell', actionExits, '#EF4444']].map(([title, rows, col]: any) => (
          <Card key={title} sx={{ border: `1px solid ${col}44` }}>
            <CardContent sx={{ p: 1.5 }}>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ color: col, mb: 1 }}>{title}</Typography>
              {rows.length === 0 ? <Typography variant="caption" color="text.disabled">None right now.</Typography> :
                rows.slice(0, 10).map((r: any) => {
                  const ac = r.action.startsWith('SELL') ? '#EF4444' : r.action.startsWith('TRIM') ? '#F59E0B'
                    : r.action.startsWith('HOLD') ? '#3B82F6' : '#22C55E';
                  return (
                    <Box key={r.symbol} sx={{ py: 0.8, borderBottom: '1px solid', borderColor: 'divider' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap' }}>
                        <Tooltip title={`conviction ${r.conviction}/95`}>
                          <Chip size="small" label={r.conviction} sx={{ height: 18, fontSize: '0.6rem', bgcolor: `${ac}22`, color: ac, fontWeight: 'bold' }} />
                        </Tooltip>
                        <Ticker symbol={r.symbol} meta={meta} />
                        <Typography variant="caption" color="text.secondary"><PriceCell price={r.price} asOf={r.as_of} /></Typography>
                        <Chip size="small" label={r.action} sx={{ height: 18, fontSize: '0.58rem', bgcolor: `${ac}18`, color: ac }} />
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.4, flexWrap: 'wrap', mt: 0.3 }}>
                        {r.badges.map((b: string) => (
                          <Chip key={b} size="small" label={b} sx={{ height: 15, fontSize: '0.52rem', bgcolor: '#ffffff10', color: 'text.secondary' }} />
                        ))}
                      </Box>
                      <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.62rem', display: 'block', mt: 0.2 }}>{r.why}</Typography>
                      {r.market_warning && (
                        <Typography variant="caption" sx={{ fontSize: '0.62rem', display: 'block', color: '#F59E0B' }}>{r.market_warning}</Typography>
                      )}
                    </Box>
                  );
                })}
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Telegram delivery card */}
      <Card sx={{ mb: 3, border: `1px solid ${ACCENT}33`, background: `linear-gradient(135deg, ${ACCENT}10, transparent)` }}>
        <CardContent sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Telegram sx={{ color: '#229ED9', fontSize: 28 }} />
            <Box>
              <Typography variant="subtitle2" fontWeight="bold">Daily digest to Telegram (free)</Typography>
              {tg?.configured ? (
                <Typography variant="caption" color="success.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CheckCircle sx={{ fontSize: 14 }} /> Configured — auto-sends daily at 5 PM ET. Send now anytime.
                </Typography>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  Not configured. Create a bot via @BotFather, then set <code>TELEGRAM_BOT_TOKEN</code> and{' '}
                  <code>TELEGRAM_CHAT_ID</code> in <code>backend/.env</code> and restart.
                </Typography>
              )}
            </Box>
          </Box>
          <Tooltip title={tg?.configured ? 'Send today\'s digest now' : 'Configure Telegram first'}>
            <span>
              <Button variant="contained" startIcon={sending ? <CircularProgress size={14} color="inherit" /> : <Send />}
                onClick={send} disabled={!tg?.configured || sending}
                sx={{ bgcolor: '#229ED9', '&:hover': { bgcolor: '#1b86ba' } }}>
                Send to Telegram
              </Button>
            </span>
          </Tooltip>
        </CardContent>
      </Card>

      {/* Highest-conviction: momentum AND ≥1 other independent feature agree */}
      {(() => {
        const overlap = picks.filter((p: any) =>
          p.source_labels?.includes('Momentum leader') && p.consensus_count >= 2);
        if (overlap.length === 0) return null;
        return (
          <Card sx={{ mb: 3, border: '1px solid #22C55E55', background: 'linear-gradient(135deg, #22C55E18, transparent)' }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 0.7, color: '#22C55E', mb: 1 }}>
                <RocketLaunch sx={{ fontSize: 18 }} /> Highest conviction: Momentum × Consensus
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                Strong uptrend leaders that <strong>another independent feature also flags</strong> — durable trend + confirmation. The best of both radars.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {overlap.map((p: any) => (
                  <Box key={p.symbol} sx={{ px: 1.5, py: 0.8, borderRadius: 1.5, border: '1px solid #22C55E44', bgcolor: '#22C55E10' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                      <Ticker symbol={p.symbol} meta={meta} color="#22C55E" />
                      <Typography variant="caption" color="text.secondary"><PriceCell price={p.price} asOf={p.as_of} /></Typography>
                      <Chip size="small" label={`${p.consensus_count}×`} color="success" sx={{ height: 16, fontSize: '0.58rem' }} />
                    </Box>
                    <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.58rem' }}>{p.source_labels.join(' · ')}</Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        );
      })()}

      {/* Consensus top picks */}
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Insights sx={{ color: ACCENT }} /> Consensus Top Picks
      </Typography>
      <SectionMeta genAt={sectGenAt.picks} />
      <Card sx={{ mb: 4 }}>
        <CardContent sx={{ p: 0 }}>
          <Table size="small">
            <TableHead><TableRow>
              {['#', 'Symbol', 'Price', 'Signals that agree', 'Consensus', 'Detail'].map(h => (
                <TableCell key={h} sx={{ fontWeight: 'bold' }}>{h}</TableCell>
              ))}
            </TableRow></TableHead>
            <TableBody>
              {picks.map((p, i) => {
                const eng = p.detail?.engine;
                const ov = p.detail?.oversold_reversion;
                return (
                  <TableRow key={p.symbol} hover>
                    <TableCell sx={{ color: 'text.disabled' }}>{i + 1}</TableCell>
                    <TableCell><Ticker symbol={p.symbol} meta={meta} /></TableCell>
                    <TableCell><PriceCell price={p.price} asOf={p.as_of} /></TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {p.source_labels.map((l: string) => (
                          <Chip key={l} size="small" label={l}
                            sx={{ height: 18, fontSize: '0.6rem', bgcolor: `${SRC_COLOR[l] || '#888'}22`, color: SRC_COLOR[l] || '#888' }} />
                        ))}
                        {p.exit_conflict && (
                          <Tooltip title={`Conflict: the exit scanner says ${p.exit_conflict} — ${p.exit_reason || 'uptrend broke'}. Consensus counts buy-side votes only; for holders the exit signal wins (see Action Board).`} arrow>
                            <Chip size="small" label={`⚠ exit: ${p.exit_conflict}`}
                              sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700,
                                    bgcolor: p.exit_conflict === 'SELL' ? '#EF444422' : '#F59E0B22',
                                    color: p.exit_conflict === 'SELL' ? '#EF4444' : '#F59E0B' }} />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={`${p.consensus_count}× · ${p.consensus_score}`}
                        color={p.consensus_count >= 3 ? 'success' : p.consensus_count === 2 ? 'primary' : 'default'}
                        sx={{ height: 20, fontSize: '0.65rem' }} />
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
                      {eng?.expected_return_pct != null && `▲ ${eng.expected_return_pct.toFixed(1)}% target`}
                      {eng?.expected_return_pct != null && ov ? ' · ' : ''}
                      {ov && `${ov.position_in_range}% of range`}
                    </TableCell>
                  </TableRow>
                );
              })}
              {picks.length === 0 && <TableRow><TableCell colSpan={6} align="center" sx={{ py: 3, color: 'text.disabled' }}>No picks yet — run the daily scans first.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Emerging breakouts — catch it BEFORE it flies */}
      <Box sx={{ mb: 0.5 }}>
        <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <RocketLaunch sx={{ color: '#A855F7' }} /> Emerging Breakouts — before it flies
        </Typography>
        <SectionMeta genAt={sectGenAt.emerging} />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
        <InfoOutlined sx={{ fontSize: 16, mt: '2px' }} />
        Stocks <strong>breaking out of a long base</strong> with volume just waking up — caught <em>before</em> the parabolic move.
        Grouped by category: each has a different strategy and time horizon.
      </Typography>

      {(['future_tech', 'sector_momentum', 'tactical'] as const).map(cat => {
        const group = emerging.filter((b: any) => b.breakout_category === cat);
        if (group.length === 0) return null;

        const catMeta: Record<string, { icon: string; color: string; label: string; desc: string; strategy: string; hold: string; exitConditions: string[]; transition: string }> = {
          future_tech: {
            icon: '🔬', color: '#A855F7', label: 'Future Technology', strategy: '📈 Long-term hold',
            desc: 'Disruptive technology with multi-year adoption curve — potential long-term compounder.',
            hold: '12–36+ months',
            exitConditions: ['Earnings reveal thesis is broken', 'Company pivots away from core tech', 'Market cap exceeds $30B (no longer emerging)'],
            transition: 'Two routes in: a cited article + LLM confirmation (link shown under "Recommended in"), or a keyword match on the business description (labelled as such — weaker evidence). Cited picks expire 21 days after their last article confirmation.',
          },
          sector_momentum: {
            icon: '⚡', color: '#3B82F6', label: 'Sector Momentum', strategy: '🚀 Momentum follow',
            desc: 'Quality sector (tech/healthcare/financials) with structural growth tailwind. Ride the trend as long as it holds.',
            hold: '4–8 weeks',
            exitConditions: ['Daily close below MA20 (first warning)', 'Price drops >8% from your entry', 'RSI falls below 50 after being overbought', 'Volume surge dries back to 1.0× (buyers gone)', 'Appears in the Exit/Sell section below'],
            transition: 'Does NOT move to Tactical — when breakout criteria fail (drops >8% off high, loses MA50, volume fades) the stock exits the list entirely.',
          },
          tactical: {
            icon: '⏱', color: '#F59E0B', label: 'Tactical Breakout', strategy: '⚡ Quick profit',
            desc: 'Pure technical pattern with no structural growth thesis. Treat as a short-term trade.',
            hold: '1–3 weeks',
            exitConditions: ['Hit +10–15% gain → take profit', 'First high-volume red candle after the move', 'Hard stop at –5% from entry', 'Appears in the Exit/Sell section below'],
            transition: 'Does NOT move to Sector Momentum — sector classification is fixed at signal time.',
          },
        };
        const m = catMeta[cat];

        return (
          <Box key={cat} sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ color: m.color }}>
                {m.icon} {m.label}
              </Typography>
              <Chip size="small" label={m.strategy}
                sx={{ height: 18, fontSize: '0.6rem', bgcolor: m.color + '22', color: m.color, fontWeight: 700 }} />
              <Chip size="small" label={`Hold: ${m.hold}`}
                sx={{ height: 18, fontSize: '0.6rem', bgcolor: '#ffffff11', color: 'text.secondary', fontWeight: 600 }} />
            </Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
              {m.desc}
            </Typography>
            <Box sx={{ mb: 1, pl: 0.5, borderLeft: `2px solid ${m.color}44` }}>
              <Typography variant="caption" color="text.disabled" display="block" sx={{ fontSize: '0.65rem', mb: 0.25 }}>
                Exit when: {m.exitConditions.join(' · ')}
              </Typography>
              <Typography variant="caption" color="text.disabled" display="block" sx={{ fontSize: '0.62rem', fontStyle: 'italic' }}>
                {m.transition}
              </Typography>
            </Box>
            <Card>
              <CardContent sx={{ p: 0 }}>
                <Table size="small">
                  <TableHead><TableRow>
                    {['', 'Symbol', 'Price', 'Score', '1-mo', '6-mo', 'Off high', 'Vol',
                      cat === 'future_tech' ? 'Theme' : 'Sector',
                      ...(cat === 'future_tech' ? ['Recommended in'] : [])].map((h, i) => (
                      <TableCell key={h || `sp${i}`} sx={{ fontWeight: 'bold' }}>{h}</TableCell>
                    ))}
                  </TableRow></TableHead>
                  <TableBody>
                    {group.map((b: any) => (
                      <TableRow key={b.symbol} hover>
                        <TableCell sx={{ width: 34, pr: 0 }}>
                          <CompanyInfoButton symbol={b.symbol} color={m.color} />
                        </TableCell>
                        <TableCell><Ticker symbol={b.symbol} meta={meta} /></TableCell>
                        <TableCell><PriceCell price={b.price} asOf={b.as_of} /></TableCell>
                        <TableCell>
                          <Chip size="small" label={b.score}
                            sx={{ height: 18, fontSize: '0.62rem', bgcolor: m.color + '22', color: m.color }} />
                        </TableCell>
                        <TableCell sx={{ color: b.ret_1m_pct == null ? 'text.disabled' : b.ret_1m_pct >= 0 ? '#22C55E' : '#EF4444', fontSize: '0.78rem' }}>
                          {b.ret_1m_pct == null ? '—' : `${b.ret_1m_pct >= 0 ? '+' : ''}${b.ret_1m_pct}%`}
                        </TableCell>
                        <TableCell sx={{ color: b.ret_6m_pct == null ? 'text.disabled' : b.ret_6m_pct >= 0 ? '#22C55E' : '#EF4444', fontSize: '0.78rem' }}>
                          {b.ret_6m_pct == null ? '—' : `${b.ret_6m_pct >= 0 ? '+' : ''}${b.ret_6m_pct}%`}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>{b.pct_off_52w_high == null ? '—' : `${b.pct_off_52w_high}%`}</TableCell>
                        <TableCell sx={{ fontSize: '0.78rem', color: b.vol_surge == null ? 'text.disabled' : b.vol_surge >= 1.5 ? m.color : 'text.secondary' }}>
                          {b.vol_surge == null ? '—' : `${b.vol_surge}×`}
                        </TableCell>
                        <TableCell>
                          {cat === 'future_tech' && b.theme
                            ? <Chip size="small" label={b.theme}
                                sx={{ height: 18, fontSize: '0.6rem', bgcolor: '#A855F722', color: '#A855F7' }} />
                            : <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.66rem' }}>
                                {b.sector || b.industry || '—'}
                              </Typography>}
                        </TableCell>
                        {cat === 'future_tech' && (
                          <TableCell sx={{ maxWidth: 170 }}>
                            <SourceArticles articles={b.source_articles} provenance={b.provenance} />
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Box>
        );
      })}
      {emerging.length === 0 && (
        <Card sx={{ mb: 4 }}>
          <CardContent sx={{ py: 3, textAlign: 'center', color: 'text.disabled' }}>
            No early-stage breakouts match right now.
          </CardContent>
        </Card>
      )}

      {/* Momentum / emerging leaders */}
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
        <RocketLaunch sx={{ color: '#22C55E' }} /> Momentum / Emerging-Leader Radar
      </Typography>
      <SectionMeta genAt={sectGenAt.momentum} />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
        <InfoOutlined sx={{ fontSize: 16, mt: '2px' }} />
        The inverse of the oversold radar — stocks <strong>near 52-week highs in strong uptrends</strong> with
        volume confirmation. This is the class of stock (like NBIS) that mean-reversion tools miss.
      </Typography>
      <Card sx={{ mb: 4 }}>
        <CardContent sx={{ p: 0 }}>
          <Table size="small">
            <TableHead><TableRow>
              {['Symbol', 'Price', 'Momentum', '3-mo', '6-mo', 'Off 52w-high'].map(h => (
                <TableCell key={h} sx={{ fontWeight: 'bold' }}>{h}</TableCell>
              ))}
            </TableRow></TableHead>
            <TableBody>
              {momentum.map(l => (
                <TableRow key={l.symbol} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                      <Ticker symbol={l.symbol} meta={meta} />
                      {l.at_new_high && <Chip size="small" label="new high" color="success" sx={{ height: 16, fontSize: '0.55rem' }} />}
                    </Box>
                  </TableCell>
                  <TableCell><PriceCell price={l.price} asOf={l.as_of} /></TableCell>
                  <TableCell><Chip size="small" label={l.momentum_score} color={l.momentum_score >= 90 ? 'success' : 'primary'} sx={{ height: 18, fontSize: '0.62rem' }} /></TableCell>
                  <TableCell sx={{ color: '#22C55E', fontSize: '0.78rem' }}>{l.ret_3m_pct != null ? `+${l.ret_3m_pct}%` : '—'}</TableCell>
                  <TableCell sx={{ color: '#22C55E', fontSize: '0.78rem' }}>{l.ret_6m_pct != null ? `+${l.ret_6m_pct}%` : '—'}</TableCell>
                  <TableCell sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>{l.pct_off_52w_high}%</TableCell>
                </TableRow>
              ))}
              {momentum.length === 0 && <TableRow><TableCell colSpan={6} align="center" sx={{ py: 3, color: 'text.disabled' }}>No momentum leaders in the latest scan.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Exit / Sell signals — when to take profit */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1, mb: 0 }}>
        <Box>
          <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TrendingDown sx={{ color: '#EF4444' }} /> Exit / Sell Signals — when it's probably peaking
          </Typography>
          <SectionMeta genAt={sectGenAt.exits} />
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          <TextField size="small" placeholder="check tickers e.g. NBIS,AAPL" value={exitInput}
            onChange={e => setExitInput(e.target.value.toUpperCase())}
            onKeyDown={e => e.key === 'Enter' && checkExits()} sx={{ width: 230 }} />
          <Button size="small" variant="outlined" onClick={checkExits} sx={{ color: '#EF4444', borderColor: '#EF444455' }}>Check</Button>
        </Box>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
        <InfoOutlined sx={{ fontSize: 16, mt: '2px' }} />
        Trend-trailing exits so a peak can come months out: <strong>HOLD</strong> while above the 50-day MA (let it run) ·
        <strong> TRIM</strong> when overbought/extended (take some profit, tighten stop) · <strong>SELL</strong> when the trend
        breaks (below 50-day MA) or it drops ≥22% from its peak. Scans the leaders we flagged; type tickers to check your own holdings.
      </Typography>
      <Card sx={{ mb: 4 }}>
        <CardContent sx={{ p: 0 }}>
          <Table size="small">
            <TableHead><TableRow>
              {['Action', 'Symbol', 'Price', 'Off high', 'vs MA50', 'RSI', 'Why'].map(h => (
                <TableCell key={h} sx={{ fontWeight: 'bold' }}>{h}</TableCell>
              ))}
            </TableRow></TableHead>
            <TableBody>
              {exits.map(s => {
                const col = s.status === 'SELL' ? '#EF4444' : s.status === 'TRIM' ? '#F59E0B' : '#22C55E';
                return (
                  <TableRow key={s.symbol} hover>
                    <TableCell><Chip size="small" label={s.status} sx={{ height: 20, fontSize: '0.65rem', fontWeight: 'bold', bgcolor: `${col}22`, color: col }} /></TableCell>
                    <TableCell><Ticker symbol={s.symbol} meta={meta} /></TableCell>
                    <TableCell><PriceCell price={s.price} asOf={s.as_of} /></TableCell>
                    <TableCell sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>{s.pct_off_52w_high}%</TableCell>
                    <TableCell sx={{ fontSize: '0.78rem', color: s.pct_vs_ma50 < 0 ? '#EF4444' : 'text.secondary' }}>{s.pct_vs_ma50 > 0 ? '+' : ''}{s.pct_vs_ma50}%</TableCell>
                    <TableCell sx={{ fontSize: '0.78rem', color: s.rsi14 >= 80 ? '#EF4444' : 'text.secondary' }}>{s.rsi14 ?? '—'}</TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', color: 'text.secondary', maxWidth: 320 }}>{s.reason}</TableCell>
                  </TableRow>
                );
              })}
              {exits.length === 0 && <TableRow><TableCell colSpan={7} align="center" sx={{ py: 3, color: 'text.disabled' }}>No tracked leaders yet — buy signals seed this, or type tickers above to check your holdings.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Rotation / oversold radar */}
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
        <TrendingDown sx={{ color: '#F59E0B' }} /> Rotation / Oversold-Reversion Radar
      </Typography>
      <SectionMeta genAt={sectGenAt.rotation} />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
        <InfoOutlined sx={{ fontSize: 16, mt: '2px' }} />
        Stocks at the bottom of their volatility range with <strong>no negative news</strong> — likely flow-driven
        dips (e.g. capital rotating into a hot IPO), candidates for mean reversion. Not a buy signal on its own.
      </Typography>
      <Card>
        <CardContent sx={{ p: 0 }}>
          <Table size="small">
            <TableHead><TableRow>
              {['Symbol', 'Price', 'Position in range', 'Signal', 'News'].map(h => (
                <TableCell key={h} sx={{ fontWeight: 'bold' }}>{h}</TableCell>
              ))}
            </TableRow></TableHead>
            <TableBody>
              {rotation.map(c => (
                <TableRow key={c.symbol} hover>
                  <TableCell><Ticker symbol={c.symbol} meta={meta} /></TableCell>
                  <TableCell><PriceCell price={c.price} asOf={c.as_of} /></TableCell>
                  <TableCell sx={{ color: c.position_in_range < 0 ? '#F87171' : 'text.secondary' }}>
                    {c.position_in_range}% {c.position_in_range < 0 ? '(below range)' : ''}
                  </TableCell>
                  <TableCell><Chip size="small" label={c.recommendation} color="success" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} /></TableCell>
                  <TableCell sx={{ fontSize: '0.72rem', color: 'text.disabled' }}>
                    {c.news_direction == null ? 'no signal' : c.news_direction.toFixed(2)}
                  </TableCell>
                </TableRow>
              ))}
              {rotation.length === 0 && <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3, color: 'text.disabled' }}>No oversold dips found in the latest scan.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Divider sx={{ my: 3 }} />
      <Typography variant="caption" color="text.disabled">
        Aggregated from your daily volatility scan, recommendation engine, news sentiment, and media-trending data.
        Consensus = number of <em>independent</em> features flagging the same ticker. Research only — not financial advice.
      </Typography>

      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast(null)} message={toast || ''} />
    </Box>
  );
}
